import api from '@root/src/api/api'
import QRCode from 'qrcode'
import { PROVIDER_TYPES } from '@root/src/vars/general'

/**
 * Канареечный переезд со сторонних провайдеров на наши (dh-whatsapp, dh-telegram, dh-max).
 *
 * Там, где переезд включён, вместо QR старого провайдера показываем QR нашего
 * инстанса — даже если старый провайдер авторизован. Старый продолжает отправлять, пока
 * канал не переключат (переключение отдельно, руками). Бэк: GET
 * networkFixer/channel/dh-migration/qr, план — dh-whatsapp-new/docs/dh-migration-canary.md.
 *
 * Переезжающий канал НЕ должен ходить в старые QR-ручки (getWaBasisQR, getDhTgQr, getMaxQr):
 * они переписывают канал на своего провайдера.
 */

/** Провайдеры, с которых переезжаем. С остальных переезжать некуда. */
const DH_MIGRATION_SOURCES: string[] = [
    PROVIDER_TYPES.whatsappBasis,
    PROVIDER_TYPES.whatsappApi,
    PROVIDER_TYPES.telegramApi,
    PROVIDER_TYPES.maxBasis,
]

/**
 * Включён ли переезд — решает бэк: поле `isDhMigration` у проекта или дефолт мессенджера.
 * Фронт хранить это условие не должен, иначе включение живёт в двух местах и дефолт с бэка
 * до интерфейса не доходит. Поэтому для канала на стороннем провайдере просто пробуем
 * ручку переезда, а на `migration_disabled` уходим в обычный флоу (см. FALLBACK_ERRORS).
 */
export function canTryDhMigration(providerType: string | null | undefined): boolean {
    return Boolean(providerType && DH_MIGRATION_SOURCES.includes(providerType))
}

/**
 * Ответы бэка, при которых переезд к каналу не относится и надо вести себя как раньше:
 * переезд выключен, провайдер канала не из списка, канал уже на нашем провайдере и т.п.
 * (например, коннектор в сторе ещё со старым providerType, а канал уже переключили).
 */
const FALLBACK_ERRORS = new Set([
    'migration_disabled',
    'provider_not_eligible',
    // Сторонний не авторизован: такой канал ведёт обычный флоу, он же удалит
    // инстанс стороннего. Бесшовный переезд — только для живых.
    'provider_not_authorized',
    // Канал закреплён за сторонним рычагом поддержки — ведёт обычный флоу.
    'forced_provider',
    'unsupported_channel_type',
    'channel_type_mismatch',
    // Двухфакторку переезд не умеет — её ведёт обычный флоу.
    'password_needed_unsupported',
])


/**
 * `not_applicable` — переезд к этому каналу не относится (выключен, не тот провайдер,
 * рычаг поддержки, уже на нашем). `unavailable` — ручка не ответила: сеть, 500, ручки нет.
 * Разница важна модалке: «не относится» можно считать закрытым вопросом, а «не ответила» —
 * нет. 05.10 модалка не различала, и на ошибке писала «Все каналы переподключены».
 */
export type DhMigrationFallbackReason = 'not_applicable' | 'unavailable'

export type DhMigrationQrState =
    | { status: 'fallback'; reason: DhMigrationFallbackReason }
    | { status: 'waiting' }
    | { status: 'ready'; imageSrc: string }
    | { status: 'authorized' }
    | { status: 'error'; message: string }

export async function fetchDhMigrationQr(connectorId: string): Promise<DhMigrationQrState> {
    const { data, error } = await api.networkFixer.getDhMigrationQr({ connectorId })

    // Любой отказ — повод вести канал обычным путём, а не показывать ошибку.
    // Ручки переезда может не быть вовсе (фронт уехал раньше бэка — тогда прилетит
    // «404 Not Found»), а двухфакторку переезд не умеет, и клиенту от такого
    // сообщения всё равно никакой пользы: в обычном флоу она обрабатывается.
    if (error) {
        const code = typeof error === 'string' ? error : String(error?.error ?? '')
        return {
            status: 'fallback',
            reason: FALLBACK_ERRORS.has(code) ? 'not_applicable' : 'unavailable',
        }
    }

    switch (data?.status) {
        case 'already_on_target':
            return { status: 'fallback', reason: 'not_applicable' }
        case 'authorized':
            return { status: 'authorized' }
        case 'ready': {
            if (!data.qr) return { status: 'waiting' }
            // dh-whatsapp отдаёт сырую строку Baileys, dh-telegram и dh-max — base64 PNG.
            if (data.qrFormat === 'raw') {
                return { status: 'ready', imageSrc: await QRCode.toDataURL(data.qr) }
            }
            return { status: 'ready', imageSrc: `data:image/png;base64,${data.qr}` }
        }
        case 'error':
            return { status: 'error', message: data.message || 'error' }
        default:
            return { status: 'waiting' }
    }
}
